import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuthStore } from "../../store/authStore";
import { useThemeStore } from "../../store/themeStore";
import { useTourStore } from "../../store/tourStore";
import { useBreakpoint } from "../../hooks/useBreakpoint";
import { api } from "../../lib/api";
import { useT, translate } from "../../i18n";
import { useLangStore } from "../../i18n/langStore";

const PAGE_TITLES: Record<string, string> = {
  "/dashboard": "Dashboard",
  "/changes":   "Eventos detectados",
  "/sources":   "Fuentes Monitorizadas",
  "/portfolio": "Portfolio",
  "/alerts":    "Avisos por correo",
  "/admin":     "Usuarios",
  "/changelog": "Historial de versiones",
  "/profile":   "Mi perfil",
  "/notifications": "Avisos",
  "/onboarding":  "Configuración inicial",
  "/products":    "Mis Productos",
  "/competitors": "Competidores",
  "/history":     "Histórico",
  "/trends":      "Tendencia de precios",
  "/radar":       "Radar Regulatorio",
  "/upgrade":     "Planes y suscripción",
  "/prices":      "Comparador de Precios",
};

interface NotifItem {
  id: string;
  title: string;
  body: string;
  type: string;
  created_at: string;
  read: boolean;
}

const TYPE_ICON: Record<string, { svg: React.ReactElement; color: string }> = {
  info:    { color: "#3b82f6", svg: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></svg> },
  warning: { color: "#f59e0b", svg: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><path d="M12 9v4M12 17h.01"/></svg> },
  success: { color: "#22c55e", svg: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><path d="M22 4L12 14.01l-3-3"/></svg> },
  feature: { color: "#8b5cf6", svg: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3c-1.2 5.4-5.4 7.2-9 9 3.6 1.8 7.8 3.6 9 9 1.2-5.4 5.4-7.2 9-9-3.6-1.8-7.8-3.6-9-9z"/></svg> },
};

function relativeTime(iso: string, lang: "es" | "en"): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return translate(lang, "time.now");
  if (mins < 60) return translate(lang, "time.minAgo", { n: mins });
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return translate(lang, "time.hourAgo", { n: hrs });
  const days = Math.floor(hrs / 24);
  if (days === 1) return translate(lang, "time.yesterday");
  return translate(lang, "time.daysAgo", { n: days });
}

type TopBarProps = {
  showMenuToggle?: boolean;
  onMenuToggle?: () => void;
};

export default function TopBar({ showMenuToggle = false, onMenuToggle }: TopBarProps) {
  const G = useAuthStore(s => s.user?.primary_color) || "#0891b2";
  const ROLE_COLORS: Record<string, string> = { admin: "#f59e0b", analyst: G, readonly: "#64748b" };
  const t = useT();
  const lang = useLangStore(s => s.lang);
  const { user, logout } = useAuthStore();
  const impersonating = useAuthStore(s => s.impersonating);
  const { theme, toggle } = useThemeStore();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isLight = theme === "light";
  const { isMobile } = useBreakpoint();

  // User dropdown
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  // Notification bell
  const [bellOpen, setBellOpen] = useState(false);
  const bellRef = useRef<HTMLDivElement>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifs, setNotifs] = useState<NotifItem[]>([]);
  const [notifsLoading, setNotifsLoading] = useState(false);

  // Close dropdowns on outside click
  useEffect(() => {
    function handler(e: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
      if (bellRef.current && !bellRef.current.contains(e.target as Node)) {
        setBellOpen(false);
      }
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Fetch unread count
  const fetchCount = useCallback(async () => {
    if (!user) return;
    try {
      const data = await api.get<{ unread: number }>("/notifications/count");
      setUnreadCount(data.unread);
    } catch {
      // ignore
    }
  }, [user]);

  useEffect(() => {
    fetchCount();
    const timer = setInterval(fetchCount, 60000);
    return () => clearInterval(timer);
  }, [fetchCount]);

  // Fetch notifications list when bell opens
  async function openBell() {
    setBellOpen(v => !v);
    if (!bellOpen) {
      setNotifsLoading(true);
      try {
        const data = await api.get<NotifItem[]>(`/notifications?lang=${lang}`);
        setNotifs(data.slice(0, 8));
      } catch {
        // ignore
      } finally {
        setNotifsLoading(false);
      }
    }
  }

  async function markRead(id: string) {
    try {
      await api.post(`/notifications/${id}/read`, {});
      setNotifs(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
      setUnreadCount(c => Math.max(0, c - 1));
    } catch {
      // ignore
    }
  }

  async function markAll() {
    try {
      await api.post("/notifications/read-all", {});
      setNotifs(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch {
      // ignore
    }
  }

  const unreadInDropdown = notifs.filter(n => !n.read).length;

  const dropdownBase: React.CSSProperties = {
    position: "absolute",
    top: "calc(100% + 8px)",
    right: 0,
    background: "var(--clr-modal-solid)",
    border: "1px solid var(--clr-border)",
    borderRadius: 14,
    boxShadow: "0 8px 32px rgba(0,0,0,0.22)",
    zIndex: 500,
  };

  return (
    <header style={{
      height: 52,
      background: "var(--clr-topbar)",
      backdropFilter: "var(--clr-blur)",
      // El backdropFilter crea un stacking context: sin z-index propio, las
      // tarjetas del contenido (también con blur) se pintaban ENCIMA de los
      // dropdowns de la campana y el perfil en toda la plataforma.
      position: "relative",
      zIndex: 300,
      borderBottom: "1px solid var(--clr-topbar-border)",
      display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: "0 1rem 0 1.5rem", flexShrink: 0,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        {showMenuToggle && (
          <button
            onClick={onMenuToggle}
            aria-label={t("topbar.openMenu")}
            style={{
              background: "transparent", border: "none", cursor: "pointer",
              color: "var(--clr-text2)", lineHeight: 1,
              padding: "4px 6px", borderRadius: 8, display: "flex", alignItems: "center",
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" aria-hidden>
              <path d="M3.75 6.75h16.5M3.75 12h16.5M3.75 17.25h16.5" />
            </svg>
          </button>
        )}
        <span style={{ fontSize: "0.9rem", fontWeight: 600, color: "var(--clr-text2)" }}>
          {PAGE_TITLES[pathname] ? t("pageTitles." + pathname) : ""}
        </span>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {/* Ayuda: relanza el tutorial de inicio */}
        {user && !(user.role === "superadmin" && !impersonating) && (
          <button
            onClick={() => useTourStore.getState().start()}
            title={t("topbar.viewTutorial")}
            aria-label={t("topbar.viewTutorial")}
            style={{
              width: 38, height: 38, borderRadius: 12,
              background: "var(--clr-toggle-bg)",
              border: "1px solid var(--clr-border)",
              cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: "0.95rem", lineHeight: 1, fontWeight: 700,
              color: "var(--clr-text2)",
              transition: "background 0.15s",
            }}
          >?</button>
        )}

        {/* Theme toggle (iconos de línea, no emoji) */}
        <button
          onClick={toggle}
          title={isLight ? "Cambiar a modo oscuro" : "Cambiar a modo claro"}
          aria-label={isLight ? "Cambiar a modo oscuro" : "Cambiar a modo claro"}
          style={{
            width: 38, height: 38, borderRadius: 12,
            background: "var(--clr-toggle-bg)",
            border: "1px solid var(--clr-border)",
            cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "var(--clr-toggle-color)", lineHeight: 1,
            transition: "background 0.15s",
          }}
        >
          {isLight ? (
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M21.752 15.002A9.718 9.718 0 0118 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 003 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 009.002-5.998z" />
            </svg>
          ) : (
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M12 3v2.25m6.364.386l-1.591 1.591M21 12h-2.25m-.386 6.364l-1.591-1.591M12 18.75V21m-4.773-4.227l-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z" />
            </svg>
          )}
        </button>

        {/* Notification bell */}
        {user && (
          <div ref={bellRef} style={{ position: "relative" }}>
            <button
              onClick={openBell}
              title={t("topbar.notifications")}
              style={{
                width: 38, height: 38, borderRadius: 12,
                background: bellOpen ? "var(--clr-nav-active-bg, rgba(8,145,178,0.12))" : "var(--clr-toggle-bg)",
                border: "1px solid var(--clr-border)",
                cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: "1rem", lineHeight: 1, position: "relative",
                transition: "background 0.15s",
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: "var(--clr-text2)" }}>
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                <path d="M13.73 21a2 2 0 0 1-3.46 0" />
              </svg>
              {unreadCount > 0 && (
                <span style={{
                  position: "absolute", top: -4, right: -4,
                  background: "#ef4444", color: "#fff",
                  borderRadius: 20, fontSize: "0.6rem", fontWeight: 800,
                  minWidth: 16, height: 16, display: "flex", alignItems: "center", justifyContent: "center",
                  padding: "0 3px", lineHeight: 1,
                }}>
                  {unreadCount > 9 ? "9+" : unreadCount}
                </span>
              )}
            </button>

            {bellOpen && (
              <div style={{ ...dropdownBase, width: "min(360px, calc(100vw - 24px))" }}>
                {/* Header */}
                <div style={{
                  padding: "0.8rem 1rem 0.65rem",
                  borderBottom: "1px solid var(--clr-border)",
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                }}>
                  <span style={{ fontSize: "0.83rem", fontWeight: 700, color: "var(--clr-text)" }}>
                    {t("topbar.notifications")}
                    {unreadInDropdown > 0 && (
                      <span style={{ marginLeft: 7, background: G, color: "#fff", borderRadius: 20, fontSize: "0.65rem", fontWeight: 800, padding: "1px 7px" }}>
                        {unreadInDropdown}
                      </span>
                    )}
                  </span>
                  {unreadInDropdown > 0 && (
                    <button
                      onClick={markAll}
                      style={{ fontSize: "0.73rem", color: G, background: "none", border: "none", cursor: "pointer", padding: 0, opacity: 0.85 }}
                    >
                      {t("topbar.markAllRead")}
                    </button>
                  )}
                </div>

                {/* List — scrollable, explicit maxHeight so footer always shows */}
                <div style={{ maxHeight: "min(340px, 55vh)", overflowY: "auto" }}>
                  {notifsLoading ? (
                    <div style={{ padding: "1.75rem", textAlign: "center", color: "var(--clr-text4)", fontSize: "0.83rem" }}>{t("common.loading")}</div>
                  ) : notifs.length === 0 ? (
                    <div style={{ padding: "2rem", textAlign: "center", color: "var(--clr-text4)", fontSize: "0.83rem" }}>
                      <div style={{ fontSize: "1.4rem", marginBottom: 8, opacity: 0.4 }}>🔔</div>
                      {t("topbar.noNotifications")}
                    </div>
                  ) : (
                    notifs.map((n, idx) => {
                      const icon = TYPE_ICON[n.type] ?? TYPE_ICON.info;
                      const isLast = idx === notifs.length - 1;
                      return (
                        <div
                          key={n.id}
                          onClick={() => !n.read && markRead(n.id)}
                          onMouseEnter={e => { if (!n.read) (e.currentTarget as HTMLDivElement).style.background = "rgba(8,145,178,0.08)"; }}
                          onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.background = !n.read ? "rgba(8,145,178,0.04)" : "transparent"; }}
                          style={{
                            padding: "0.7rem 1rem",
                            borderBottom: isLast ? "none" : "1px solid var(--clr-border)",
                            display: "flex", gap: "0.65rem", alignItems: "flex-start",
                            cursor: !n.read ? "pointer" : "default",
                            background: !n.read ? "rgba(8,145,178,0.04)" : "transparent",
                            transition: "background 0.12s",
                          }}
                        >
                          {/* Unread dot */}
                          <div style={{ width: 7, height: 7, borderRadius: "50%", background: n.read ? "transparent" : G, flexShrink: 0, marginTop: 6 }} />
                          {/* Type icon */}
                          <span style={{ color: icon.color, flexShrink: 0, display: "flex", alignItems: "center", marginTop: 2 }}>{icon.svg}</span>
                          {/* Content */}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: n.read ? 400 : 600, fontSize: "0.83rem", color: "var(--clr-text)", marginBottom: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {n.title}
                            </div>
                            <div style={{ fontSize: "0.77rem", color: "var(--clr-text3)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {n.body.length > 72 ? n.body.slice(0, 72) + "…" : n.body}
                            </div>
                            <div style={{ fontSize: "0.7rem", color: "var(--clr-text4)", marginTop: 3 }}>
                              {relativeTime(n.created_at, lang)}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                {/* Footer — always visible */}
                <div style={{
                  padding: "0.55rem 1rem",
                  borderTop: "1px solid var(--clr-border)",
                  display: "flex", justifyContent: "center",
                }}>
                  <button
                    onClick={() => { setBellOpen(false); navigate("/notifications"); }}
                    style={{
                      fontSize: "0.78rem", color: G,
                      background: "none", border: "none", cursor: "pointer",
                      padding: "4px 12px", borderRadius: 7,
                      transition: "background 0.12s",
                    }}
                    onMouseEnter={e => (e.currentTarget.style.background = "rgba(8,145,178,0.08)")}
                    onMouseLeave={e => (e.currentTarget.style.background = "none")}
                  >
                    {t("topbar.viewAllNotifications")}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* User avatar + name → dropdown */}
        {user && (
          <div ref={userMenuRef} style={{ position: "relative" }}>
            <button
              onClick={() => setUserMenuOpen(v => !v)}
              style={{
                display: "flex", alignItems: "center", gap: 8,
                background: userMenuOpen ? "var(--clr-nav-active-bg, rgba(8,145,178,0.12))" : "transparent",
                border: "1px solid transparent",
                borderRadius: 9, padding: "3px 8px 3px 3px",
                cursor: "pointer", transition: "all 0.15s",
              }}
            >
              <div style={{ width: 28, height: 28, borderRadius: "50%", background: G + "22", border: `1px solid ${G}44`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.75rem", color: G, fontWeight: 700 }}>
                {(user.first_name ? user.first_name[0] : user.email[0]).toUpperCase()}
              </div>
              {!isMobile && (
                <div style={{ lineHeight: 1.3, textAlign: "left" }}>
                  <div style={{ fontSize: "0.78rem", color: "var(--clr-text2)", fontWeight: 500 }}>
                    {user.first_name || user.last_name
                      ? `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim()
                      : user.email}
                  </div>
                  <div style={{ fontSize: "0.65rem", color: ROLE_COLORS[user.role] ?? "#94a3b8", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>{user.role}</div>
                </div>
              )}
            </button>

            {userMenuOpen && (
              <div style={{ ...dropdownBase, minWidth: 180 }}>
                <button
                  onClick={() => { setUserMenuOpen(false); navigate("/profile"); }}
                  style={{
                    width: "100%", padding: "0.65rem 1rem",
                    background: "transparent", border: "none",
                    cursor: "pointer", textAlign: "left",
                    fontSize: "0.85rem", color: "var(--clr-text)",
                    display: "flex", alignItems: "center", gap: 8,
                    borderRadius: "14px 14px 0 0",
                    transition: "background 0.1s",
                  }}
                  onMouseEnter={e => (e.currentTarget.style.background = "var(--clr-nav-active-bg, rgba(8,145,178,0.08))")}
                  onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, opacity: 0.65 }}>
                    <path d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                  </svg>
                  {t("nav.profile")}
                </button>
                <button
                  onClick={() => {
                    setUserMenuOpen(false);
                    // Org users: las prefs viven en Alertas; el superadmin conserva la página propia
                    navigate(user?.role === "superadmin" ? "/notifications/preferences" : "/alerts?tab=prefs");
                  }}
                  style={{
                    width: "100%", padding: "0.65rem 1rem",
                    background: "transparent", border: "none",
                    cursor: "pointer", textAlign: "left",
                    fontSize: "0.85rem", color: "var(--clr-text)",
                    display: "flex", alignItems: "center", gap: 8,
                    transition: "background 0.1s",
                  }}
                  onMouseEnter={e => (e.currentTarget.style.background = "var(--clr-nav-active-bg, rgba(8,145,178,0.08))")}
                  onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, opacity: 0.65 }}>
                    <path d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
                  </svg>
                  {t("topbar.emailPreferences")}
                </button>
                <div style={{ height: 1, background: "var(--clr-border)", margin: "0 0.5rem" }} />
                <button
                  onClick={() => { logout(); navigate("/login"); }}
                  style={{
                    width: "100%", padding: "0.65rem 1rem",
                    background: "transparent", border: "none",
                    cursor: "pointer", textAlign: "left",
                    fontSize: "0.85rem", color: "#ef4444",
                    display: "flex", alignItems: "center", gap: 8,
                    borderRadius: "0 0 14px 14px",
                    transition: "background 0.1s",
                  }}
                  onMouseEnter={e => (e.currentTarget.style.background = "rgba(239,68,68,0.08)")}
                  onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, opacity: 0.8 }}>
                    <path d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75" />
                  </svg>
                  {t("topbar.logout")}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
}
